import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS } from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { sincronizar, suscribirSync, type SyncState } from '../sync/engine.js';
import { salirDePractica } from '../lib/practica.js';
import { AvisoCopiaDeTienda } from './AvisoCopiaDeTienda.js';
import { useT } from '../i18n/index.js';
import type { Clave } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Confirmar } from './Preguntar.js';

const ETIQUETAS: Record<SyncState['fase'], Clave> = {
  inactivo: 'sync.alDia',
  sincronizando: 'sync.sincronizando',
  'sin-conexion': 'sync.sinConexion',
  error: 'sync.error',
  'sin-registrar': 'sync.sinRegistrar',
  'dado-de-baja': 'sync.dadoDeBaja',
  practica: 'sync.practica',
};

const COLORES: Record<SyncState['fase'], string> = {
  inactivo: 'bg-emerald-500',
  sincronizando: 'bg-sky-500 animate-pulse',
  'sin-conexion': 'bg-amber-500',
  error: 'bg-rose-500',
  'sin-registrar': 'bg-slate-500',
  'dado-de-baja': 'bg-rose-500',
  practica: 'bg-violet-500',
};

/**
 * Always on screen, never hidden behind a menu.
 *
 * "Sin conexión" is a normal state here, not an error — the app is designed to work that way,
 * so it is shown calmly in amber rather than screamed in red. What does deserve alarm is
 * unsynced work piling up: a phone lost with forty sales on it is forty sales gone, and the
 * only defence is that somebody saw the number climbing beforehand.
 */
export function EstadoSync() {
  const t = useT();
  const [estado, setEstado] = useState<SyncState | null>(null);
  const [saliendo, setSaliendo] = useState(false);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  useEffect(() => suscribirSync(setEstado), []);
  if (!estado) return null;

  /**
   * A practice shop has nothing pending, because there is nowhere for it to go.
   *
   * The count is real — every practice event is sitting unsent in the outbox — but reporting it
   * is a lie about what it means, and "89 records have not reached the server, if you lose this
   * device you lose them" is a frightening thing to read during a lesson about nothing.
   */
  const practicando = estado.fase === 'practica';
  const pendientes = practicando ? 0 : estado.pendientes;
  const acumulando = pendientes >= config.syncPendientesAlerta;

  return (
    <div className="flex flex-col gap-2">
      {/*
        Practising, there is nothing to sync, and this is the one control on every screen that
        says the shop is pretend. So tapping it offers the way out, which otherwise lives only
        on the lessons screen, where nobody mid-sale thinks to look.
      */}
      <button
        type="button"
        onClick={() => (practicando ? setSaliendo(true) : void sincronizar())}
        className="tarjeta-accion flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className={`size-2.5 shrink-0 rounded-full ${COLORES[estado.fase]}`} />
        <span className="flex-1 text-sm font-medium">{t(ETIQUETAS[estado.fase])}</span>
        {pendientes > 0 && (
          <span
            className={`tabular rounded-lg px-2 py-1 text-xs font-semibold ${
              acumulando ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-800 text-slate-400'
            }`}
          >
            {t('sync.sinEnviar', { n: pendientes })}
          </span>
        )}
        {practicando ? (
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-violet-300">
            {t('practica.salir')}
            <Ico icono="salir" className="size-4" />
          </span>
        ) : (
          // Tapping this syncs. Without the glyph it reads as a status line, not a button.
          <Ico icono="sincronizar" className="size-4 shrink-0 text-slate-600" />
        )}
      </button>

      <AvisoCopiaDeTienda />

      {acumulando && (
        <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          {t('sync.acumulando', { n: pendientes })}
        </p>
      )}

      {estado.derivaAlta && (
        <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          {t('sync.deriva', { s: Math.round(estado.clockOffsetMs / 1000) })}
        </p>
      )}

      {estado.rechazados > 0 && (
        <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          {t('sync.rechazados', { n: estado.rechazados })}
        </p>
      )}

      {estado.fase === 'error' && estado.ultimoError && (
        <p className="px-1 text-xs text-slate-500">{estado.ultimoError}</p>
      )}

      {saliendo && (
        <Confirmar
          titulo={t('aprender.salirTitulo')}
          mensaje={t('aprender.salirMensaje')}
          confirmar={t('aprender.salirConfirmar')}
          icono="salir"
          onConfirmar={() => void salirDePractica()}
          onCerrar={() => setSaliendo(false)}
        />
      )}
    </div>
  );
}
